import { useRef, type KeyboardEvent, type PointerEvent } from "react";

/**
 * A big touch slider. Drawn rather than an <input type="range">, so dragging it never
 * takes focus from a text box being typed in (the keyboard stays up and nothing moves;
 * ui/keepTyping.ts). Still a real slider for screen readers and arrow keys.
 */
export function Slider({
  value,
  min,
  max,
  step,
  onChange,
  label,
  valueText,
}: {
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  label: string;
  valueText?: string;
}) {
  const track = useRef<HTMLSpanElement>(null);
  const clamp = (v: number) => Math.min(max, Math.max(min, Math.round((v - min) / step) * step + min));
  const at = (clientX: number) => {
    const r = track.current?.getBoundingClientRect();
    if (!r || !r.width) return value;
    return clamp(min + ((clientX - r.left) / r.width) * (max - min));
  };
  const set = (v: number) => v !== value && onChange(v);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    set(at(e.clientX));
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) set(at(e.clientX));
  };
  const onKeyDown = (e: KeyboardEvent) => {
    const big = Math.max(step, Math.round((max - min) / 10 / step) * step);
    const to: Record<string, number> = { ArrowLeft: value - step, ArrowDown: value - step, ArrowRight: value + step, ArrowUp: value + step, PageDown: value - big, PageUp: value + big, Home: min, End: max };
    if (!(e.key in to)) return;
    e.preventDefault();
    set(clamp(to[e.key]!));
  };

  const pct = max > min ? ((Math.min(max, Math.max(min, value)) - min) / (max - min)) * 100 : 0;
  return (
    <div
      className="slider"
      role="slider"
      tabIndex={0}
      aria-label={label}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-valuetext={valueText}
      style={{ ["--p" as string]: String(pct / 100) }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      // Keeps focus (and the keyboard) on the text box being typed in.
      onMouseDown={(e) => e.preventDefault()}
      onKeyDown={onKeyDown}
    >
      <span ref={track} className="slider-track" aria-hidden="true">
        <span className="slider-fill" />
      </span>
      <span className="slider-thumb" aria-hidden="true" />
    </div>
  );
}
